import { Fragment, useMemo } from 'react';

import {
  DENSITY_SUBSTANCES,
  HEATING_SEGMENTS,
  PARTICLE_BOX,
  STATE_PROPERTIES,
  WATER_HEATING,
  buildParticleLayout,
  calculateDensity,
  compressedVolumeMl,
  densityOf,
  diffusionCoefficient,
  formatNumber,
  heatingStateAt,
  particlePositionsAt,
  type CaseVisual,
  type DiffusionSetup,
  type HeatingSegment,
  type MatterState,
  type StateFractions,
} from './particleModel';

const PARTICLE_RADIUS = 2.6;
const PURE_STATE: Record<MatterState, StateFractions> = {
  solid: { solid: 1, liquid: 0, gas: 0 },
  liquid: { solid: 0, liquid: 1, gas: 0 },
  gas: { solid: 0, liquid: 0, gas: 1 },
};
const STATE_TEMPERATURE_C: Record<MatterState, number> = { solid: -10, liquid: 20, gas: 110 };
/** Water molecule drawn to the particle box scale: O radius 1.9, H radius 1.1, O–H 2.0, H–O–H about 104.5°. */
const WATER_O_RADIUS = 1.9;
const WATER_H_RADIUS = 1.1;
const WATER_H_OFFSET = { x: 1.58, y: 1.23 } as const;
/** Degrees per second: ice keeps one orientation; liquid molecules tumble slowly, steam molecules fast. */
const WATER_SPIN_DEG_PER_S: Record<MatterState, number> = { solid: 0, liquid: 40, gas: 140 };

/** One H₂O molecule: red oxygen with two white hydrogens. A teaching shape, not to scale. */
export function WaterMolecule({ x, y, angle }: { x: number; y: number; angle: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`}>
      <circle cx={-WATER_H_OFFSET.x} cy={WATER_H_OFFSET.y} r={WATER_H_RADIUS} className="fill-canvas-pure stroke-ink/60" strokeWidth="0.35" />
      <circle cx={WATER_H_OFFSET.x} cy={WATER_H_OFFSET.y} r={WATER_H_RADIUS} className="fill-canvas-pure stroke-ink/60" strokeWidth="0.35" />
      <circle r={WATER_O_RADIUS} className="fill-brand-coral stroke-ink/60" strokeWidth="0.35" />
    </g>
  );
}

/** Particle box: every circle is placed by the model from the state fractions and time. */
export function ParticleBox({
  fractions,
  temperatureC,
  time,
  label,
  compact = false,
  molecule = 'particle',
}: {
  fractions: StateFractions;
  temperatureC: number;
  time: number;
  label: string;
  compact?: boolean;
  /** `water` draws every particle as an H₂O molecule; `particle` keeps the generic circle. */
  molecule?: 'particle' | 'water';
}) {
  const { solid, liquid, gas } = fractions;
  const layout = useMemo(() => buildParticleLayout({ solid, liquid, gas }), [solid, liquid, gas]);
  const points = particlePositionsAt(layout, time, temperatureC);
  return (
    <svg
      viewBox={`0 0 ${PARTICLE_BOX.width} ${PARTICLE_BOX.height}`}
      role="img"
      aria-label={label}
      className={`w-full rounded-2xl border border-brand-sky/25 bg-wash-sky ${compact ? 'max-h-40' : 'max-h-72'}`}
    >
      <rect x="0.5" y="0.5" width={PARTICLE_BOX.width - 1} height={PARTICLE_BOX.height - 1} rx="4" className="fill-canvas-pure/60 stroke-slate2/40" strokeWidth="0.6" />
      {molecule === 'water' ? points.map((point, index) => (
        <WaterMolecule
          key={point.id}
          x={point.x}
          y={point.y}
          angle={(point.state === 'solid' ? 0 : (index * 47) % 360) + WATER_SPIN_DEG_PER_S[point.state] * time * (index % 2 === 0 ? 1 : -1)}
        />
      )) : points.map((point) => (
        <circle
          key={point.id}
          cx={point.x}
          cy={point.y}
          r={PARTICLE_RADIUS}
          className={point.state === 'solid' ? 'fill-brand-sky stroke-ink/60' : point.state === 'liquid' ? 'fill-brand-sky/80 stroke-ink/40' : 'fill-brand-sky/60 stroke-ink/30'}
          strokeWidth="0.5"
        />
      ))}
    </svg>
  );
}

export function StateBox({ state, time }: { state: MatterState; time: number }) {
  const properties = STATE_PROPERTIES[state];
  return (
    <figure className="min-w-0 rounded-2xl border border-hairline bg-canvas-pure p-3">
      <ParticleBox
        fractions={PURE_STATE[state]}
        temperatureC={STATE_TEMPERATURE_C[state]}
        time={time}
        compact
        label={`${properties.label} particle model: ${properties.arrangement}`}
      />
      <figcaption className="mt-2 text-sm font-extrabold text-ink">{properties.label}</figcaption>
      <p className="text-xs font-medium leading-5 text-slate2">{properties.arrangement}</p>
    </figure>
  );
}

const CHART = { width: 320, height: 190, left: 40, right: 12, top: 14, bottom: 30 } as const;
const CHART_MIN_C = -30;
const CHART_MAX_C = 150;
const AXIS_TICKS_C = [-30, 0, 50, 100, 150];

function chartY(temperatureC: number) {
  const span = CHART_MAX_C - CHART_MIN_C;
  return CHART.top + ((CHART_MAX_C - temperatureC) / span) * (CHART.height - CHART.top - CHART.bottom);
}

/** Heating curve plotted from the learner's model segments, with a marker at the energy added so far. */
export function HeatingCurveChart({
  segments,
  energyJ,
  temperatureC,
  massG,
}: {
  segments: HeatingSegment[];
  energyJ: number;
  temperatureC: number;
  massG: number;
}) {
  const totalJ = segments[segments.length - 1].endEnergyJ;
  const chartX = (energy: number) => CHART.left + (Math.min(energy, totalJ) / totalJ) * (CHART.width - CHART.left - CHART.right);
  const points = [
    `${chartX(0)},${chartY(segments[0].startTemperatureC)}`,
    ...segments.map((segment) => `${chartX(segment.endEnergyJ)},${chartY(segment.endTemperatureC)}`),
  ].join(' ');
  return (
    <svg
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      role="img"
      aria-label={`Heating curve of ${formatNumber(massG, 0)} g of water. Marker at ${Math.round(energyJ)} J and ${formatNumber(temperatureC)} °C.`}
      className="w-full"
    >
      <line x1={CHART.left} y1={CHART.top} x2={CHART.left} y2={CHART.height - CHART.bottom} className="stroke-slate2" strokeWidth="1" />
      <line x1={CHART.left} y1={CHART.height - CHART.bottom} x2={CHART.width - CHART.right} y2={CHART.height - CHART.bottom} className="stroke-slate2" strokeWidth="1" />
      {AXIS_TICKS_C.map((tick) => (
        <g key={tick}>
          <line x1={CHART.left - 3} y1={chartY(tick)} x2={CHART.width - CHART.right} y2={chartY(tick)} className="stroke-hairline" strokeWidth="0.6" />
          <text x={CHART.left - 6} y={chartY(tick) + 3} textAnchor="end" fontSize="9" fontWeight="700" className="fill-slate2">
            {tick} °C
          </text>
        </g>
      ))}
      <polyline points={points} fill="none" className="stroke-brand-coral" strokeWidth="3" strokeLinejoin="round" />
      {segments.filter((segment) => segment.phase === 'melting' || segment.phase === 'boiling').map((segment) => (
        <text
          key={segment.phase}
          x={(chartX(segment.startEnergyJ) + chartX(segment.endEnergyJ)) / 2}
          y={chartY(segment.startTemperatureC) - 6}
          textAnchor="middle"
          fontSize="9"
          fontWeight="800"
          className="fill-ink"
        >
          {segment.phase === 'melting' ? 'melting' : 'boiling (flat)'}
        </text>
      ))}
      <text x={(CHART.left + CHART.width) / 2} y={CHART.height - 8} textAnchor="middle" fontSize="9" fontWeight="700" className="fill-slate2">
        Energy added to {formatNumber(massG, 0)} g of water (J) →
      </text>
      <circle cx={chartX(energyJ)} cy={chartY(temperatureC)} r="6" className="fill-canvas-pure stroke-ink" strokeWidth="2.5" />
    </svg>
  );
}

const DIFFUSION_VIEW = { width: 100, height: 70 } as const;
const DYE_COUNT = 22;
const SPREAD_START = 3;
const SPREAD_SCALE = 4;
const SPREAD_MAX = 44;
const DIFFUSION_LOOP_S = 8;
const REFERENCE_DIFFUSION = diffusionCoefficient('liquid', 20) ?? 1;

function diffusionSpreadRadius(setup: DiffusionSetup, modelSeconds: number): number {
  const coefficient = diffusionCoefficient(setup.state, setup.temperatureC) ?? 0;
  return Math.min(SPREAD_MAX, SPREAD_START + SPREAD_SCALE * Math.sqrt((coefficient / REFERENCE_DIFFUSION) * modelSeconds));
}

function DiffusionBox({ setup, modelSeconds }: { setup: DiffusionSetup; modelSeconds: number }) {
  const radius = diffusionSpreadRadius(setup, modelSeconds);
  const centreX = DIFFUSION_VIEW.width / 2;
  const centreY = DIFFUSION_VIEW.height / 2;
  const ratio = (diffusionCoefficient(setup.state, setup.temperatureC) ?? 0) / REFERENCE_DIFFUSION;
  return (
    <figure className="min-w-0 rounded-2xl border border-hairline bg-canvas-pure p-3">
      <svg
        viewBox={`0 0 ${DIFFUSION_VIEW.width} ${DIFFUSION_VIEW.height}`}
        role="img"
        aria-label={`${setup.label}: dye has spread to a radius of ${Math.round(radius)} model units`}
        className="max-h-40 w-full rounded-xl bg-wash-sky"
      >
        <circle cx={centreX} cy={centreY} r={radius} className="fill-brand-coral/10 stroke-brand-coral/50" strokeDasharray="2 2" strokeWidth="0.6" />
        {Array.from({ length: DYE_COUNT }, (_, index) => {
          const angle = index * 2.399963;
          const distance = radius * Math.sqrt((index + 0.5) / DYE_COUNT);
          return (
            <circle
              key={index}
              cx={Math.min(DIFFUSION_VIEW.width - 2, Math.max(2, centreX + distance * Math.cos(angle)))}
              cy={Math.min(DIFFUSION_VIEW.height - 2, Math.max(2, centreY + distance * Math.sin(angle)))}
              r="1.8"
              className="fill-brand-coral"
            />
          );
        })}
      </svg>
      <figcaption className="mt-2 text-sm font-extrabold text-ink">{setup.label}</figcaption>
      <p className="text-xs font-medium leading-5 text-slate2">
        Model diffusion rate: {ratio >= 100 ? `about ${Math.round(ratio).toLocaleString('en-AU')}×` : `${formatNumber(ratio)}×`} water at 20 °C
      </p>
    </figure>
  );
}

export function DiffusionComparison({ first, second, time }: { first: DiffusionSetup; second: DiffusionSetup; time: number }) {
  const modelSeconds = time === 0 ? DIFFUSION_LOOP_S / 2 : time % DIFFUSION_LOOP_S;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <DiffusionBox setup={first} modelSeconds={modelSeconds} />
      <DiffusionBox setup={second} modelSeconds={modelSeconds} />
    </div>
  );
}

const SYRINGE_DOTS = 14;
/** The substance each state stands for, matching the reviewed compressibility data. */
const SYRINGE_SUBSTANCE: Record<MatterState, string> = { gas: 'Air', liquid: 'Water', solid: 'Iron' };
const COMPRESSIBILITY_NOTE: Record<MatterState, string> = {
  gas: 'Gas: Boyle’s law.',
  liquid: 'Water: compressibility about 4.6 × 10⁻¹⁰ Pa⁻¹.',
  solid: 'Iron: bulk modulus about 170 GPa.',
};

function Syringe({ state, startVolumeMl, pressureMultiplier }: { state: MatterState; startVolumeMl: number; pressureMultiplier: number }) {
  const volume = compressedVolumeMl(state, startVolumeMl, pressureMultiplier) ?? startVolumeMl;
  const fill = volume / startVolumeMl;
  const barrel = { x: 6, y: 8, width: 160, height: 26 };
  const filledWidth = barrel.width * fill;
  const name = SYRINGE_SUBSTANCE[state];
  return (
    <figure className="min-w-0 rounded-2xl border border-hairline bg-canvas-pure p-3">
      <svg viewBox="0 0 200 42" role="img" aria-label={`${name} syringe: ${startVolumeMl} mL becomes ${formatNumber(volume, state === 'gas' ? 0 : 3)} mL`} className="w-full">
        <rect x={barrel.x} y={barrel.y} width={barrel.width} height={barrel.height} rx="4" className="fill-wash-sky stroke-slate2" strokeWidth="1" />
        <rect x={barrel.x + filledWidth} y={barrel.y - 4} width="5" height={barrel.height + 8} className="fill-ink" />
        <rect x={barrel.x + filledWidth + 5} y={barrel.y + barrel.height / 2 - 2} width={barrel.width - filledWidth + 22} height="4" className="fill-slate2" />
        {Array.from({ length: SYRINGE_DOTS }, (_, index) => {
          const column = state === 'gas' ? index : index % 7;
          const row = state === 'gas' ? index % 2 : Math.floor(index / 7);
          const spacing = state === 'gas' ? filledWidth / SYRINGE_DOTS : 6;
          return (
            <circle
              key={index}
              cx={barrel.x + 4 + column * spacing}
              cy={state === 'gas' ? barrel.y + 7 + row * 12 : barrel.y + 9 + row * 8}
              r="2.6"
              className="fill-brand-sky stroke-ink/40"
              strokeWidth="0.5"
            />
          );
        })}
      </svg>
      <figcaption className="mt-1 text-sm font-extrabold text-ink">
        {name}: {startVolumeMl} mL → {formatNumber(volume, state === 'gas' ? 0 : 3)} mL
      </figcaption>
    </figure>
  );
}

const DEFAULT_SYRINGE_STATES: MatterState[] = ['gas', 'liquid'];

export function CompressionComparison({
  startVolumeMl,
  pressureMultiplier,
  states = DEFAULT_SYRINGE_STATES,
}: {
  startVolumeMl: number;
  pressureMultiplier: number;
  states?: MatterState[];
}) {
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        {states.map((state) => (
          <Syringe key={state} state={state} startVolumeMl={startVolumeMl} pressureMultiplier={pressureMultiplier} />
        ))}
      </div>
      <p className="mt-2 text-xs font-medium leading-5 text-slate2">
        Pressure × {pressureMultiplier} at constant temperature. {states.map((state) => COMPRESSIBILITY_NOTE[state]).join(' ')}
      </p>
    </div>
  );
}

const LOG_MIN = -4;
const LOG_MAX = 2;

function logWidth(density: number): number {
  return Math.max(2, ((Math.log10(density) - LOG_MIN) / (LOG_MAX - LOG_MIN)) * 100);
}

function SampleDensityRow({ density }: { density: number }) {
  return (
    <li className="grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] items-center gap-2 rounded-lg bg-wash-sunshine text-xs sm:grid-cols-[10rem_minmax(0,1fr)]" data-testid="density-sample-row">
      <span className="truncate font-extrabold text-ink">★ Your sample</span>
      <span className="flex min-w-0 items-center gap-2">
        <span
          className="h-3 rounded-full border-2 border-dashed border-ink bg-brand-sunshine"
          style={{ width: `${Math.min(100, logWidth(density)) * 0.7}%` }}
          aria-hidden="true"
        />
        <span className="shrink-0 font-extrabold text-ink">{density < 0.01 ? density.toFixed(5) : formatNumber(density, density >= 10 ? 1 : 3)} g/cm³</span>
      </span>
    </li>
  );
}

/** Density bars on a log scale, so air and gold fit on one chart. Values are always printed. */
export function DensityChart({
  highlightIds,
  massG,
  volumeCm3,
  sampleDensity,
}: {
  highlightIds: string[];
  massG?: number;
  volumeCm3?: number;
  /** The learner's own sample (Explore), drawn as an extra bar in density order. */
  sampleDensity?: number;
}) {
  const water = densityOf('water');
  const measured = massG !== undefined && volumeCm3 !== undefined ? calculateDensity(massG, volumeCm3) : null;
  // The sample bar sits before the first listed substance at least as dense, or last.
  const sampleIndex = sampleDensity === undefined ? -1 : DENSITY_SUBSTANCES.findIndex((item) => item.densityGPerCm3 >= sampleDensity);
  return (
    <div>
      {measured !== null && massG !== undefined && volumeCm3 !== undefined ? (
        <p className="mb-3 rounded-2xl bg-wash-sunshine px-4 py-3 text-sm font-extrabold text-ink">
          Density = mass ÷ volume = {formatNumber(massG)} g ÷ {formatNumber(volumeCm3)} cm³ = {formatNumber(measured, 2)} g/cm³
        </p>
      ) : null}
      {massG === undefined && volumeCm3 !== undefined ? (
        <p className="mb-3 rounded-2xl bg-wash-sunshine px-4 py-3 text-sm font-extrabold text-ink">
          Mass = density × volume, with volume = {formatNumber(volumeCm3)} cm³:{' '}
          {highlightIds
            .flatMap((id) => {
              const item = densityOf(id);
              return item ? [`${item.name} ${formatNumber(item.densityGPerCm3 * volumeCm3, 0)} g`] : [];
            })
            .join(' · ')}
        </p>
      ) : null}
      <ul className="space-y-1.5" aria-label="Density of reviewed substances, log scale">
        {DENSITY_SUBSTANCES.map((item, index) => {
          const highlighted = highlightIds.includes(item.id);
          const floats = water ? item.densityGPerCm3 < water.densityGPerCm3 : false;
          return (
            <Fragment key={item.id}>
            {sampleDensity !== undefined && index === sampleIndex ? <SampleDensityRow density={sampleDensity} /> : null}
            <li className="grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] items-center gap-2 text-xs sm:grid-cols-[10rem_minmax(0,1fr)]">
              <span className={`truncate ${highlighted ? 'font-extrabold text-ink' : 'font-bold text-slate2'}`}>
                {highlighted ? '▶ ' : ''}
                {item.name}
              </span>
              <span className="flex min-w-0 items-center gap-2">
                <span className={`h-3 rounded-full ${highlighted ? 'bg-brand-coral' : 'bg-stone2'}`} style={{ width: `${logWidth(item.densityGPerCm3) * 0.7}%` }} aria-hidden="true" />
                <span className={`shrink-0 ${highlighted ? 'font-extrabold text-ink' : 'font-bold text-slate2'}`}>
                  {item.densityGPerCm3 < 0.01 ? item.densityGPerCm3.toFixed(5) : item.densityGPerCm3.toFixed(item.densityGPerCm3 >= 10 ? 2 : 3)} g/cm³
                  {item.id === 'water' ? ' (reference)' : floats ? ' · floats' : ' · sinks'}
                </span>
              </span>
            </li>
            </Fragment>
          );
        })}
        {sampleDensity !== undefined && sampleIndex === -1 ? <SampleDensityRow density={sampleDensity} /> : null}
      </ul>
      <p className="mt-2 text-xs font-medium leading-5 text-slate2">
        Log scale: each step to the right is about ten times denser. “Floats/sinks” compares with liquid water at 20 °C; a gas rises or falls in air rather than floating on water.
      </p>
    </div>
  );
}

export function CaseVisualPanel({ visual, time }: { visual: CaseVisual; time: number }) {
  switch (visual.kind) {
    case 'states':
      return (
        <div className={`grid gap-3 ${visual.states.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'max-w-sm'}`}>
          {visual.states.map((state) => (
            <StateBox key={state} state={state} time={time} />
          ))}
        </div>
      );
    case 'heating':
    {
      // Prediction cases use the fixed reviewed 1 g sample.
      const state = heatingStateAt(visual.energyJ);
      return state ? (
        <HeatingCurveChart segments={HEATING_SEGMENTS} energyJ={visual.energyJ} temperatureC={state.temperatureC} massG={WATER_HEATING.sampleMassG} />
      ) : null;
    }
    case 'diffusion':
      return <DiffusionComparison first={visual.first} second={visual.second} time={time} />;
    case 'compression':
      return <CompressionComparison startVolumeMl={visual.startVolumeMl} pressureMultiplier={visual.pressureMultiplier} />;
    default:
      return <DensityChart highlightIds={visual.substanceIds} massG={visual.massG} volumeCm3={visual.volumeCm3} />;
  }
}
